import { useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import { logger } from "../utils/logger.js";

const API_BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { setToken, setUser, user, loading, token } = useAuth();

  logger.log("AuthCallback: Component mounted");

  // Wrap updateUserAccess in useCallback to memoize it
  const updateUserAccess = useCallback(
    async (tokenToUse, accessPrefs) => {
      logger.log("AuthCallback: Updating user access with prefs:", accessPrefs);

      try {
        const response = await axios.post(
          `${API_BASE_URL}/auth/update-access`,
          accessPrefs,
          {
            headers: { Authorization: `Bearer ${tokenToUse}` },
          },
        );

        logger.log("AuthCallback: Access update response received");

        // Update with new token that includes access permissions
        const newToken = response.data.token;
        setToken(newToken);

        logger.log("AuthCallback: Updated token, clearing sessionStorage");

        // Clear saved access preferences
        sessionStorage.removeItem("moduleAccess");

        logger.log("AuthCallback: Navigating to dashboard after access update");

        // UPDATED: Role-based redirect after access update
        const decoded = newToken ? jwtDecode(newToken) : null;
        const isTeacher = decoded && decoded.user_type === "teacher";

        logger.log(
          "AuthCallback: User role after access update:",
          isTeacher ? "teacher" : "student",
        );
        navigate(isTeacher ? "/teacher/dashboard" : "/dashboard");
      } catch (error) {
        console.error(
          "AuthCallback: Error updating access:",
          error.response?.data || error.message,
        );
        // Even if update fails, still navigate to dashboard with role-based logic
        logger.log("AuthCallback: Navigating to dashboard despite error");

        // UPDATED: Role-based redirect even on error
        const decoded = tokenToUse ? jwtDecode(tokenToUse) : null;
        const isTeacher = decoded && decoded.user_type === "teacher";

        navigate(isTeacher ? "/teacher/dashboard" : "/dashboard");
      }
    },
    [setToken, navigate],
  );

  // Initial effect — read user from cookie via verify-token
  // The backend sets an HTTP-only 'access_token' cookie during Google OAuth,
  // so we never receive the token in the URL anymore.
  useEffect(() => {
    logger.log("AuthCallback: Verifying session via cookie...");
    const isNewUser = params.get("new_user") === "true";
    logger.log("AuthCallback: Is new user:", isNewUser);

    const linkedProvider = params.get("linked");
    if (linkedProvider) {
      sessionStorage.setItem("provider_just_linked", linkedProvider);
    }

    axios
      .get(`${API_BASE_URL}/auth/verify-token`, {
        withCredentials: true, // sends the HTTP-only access_token cookie
      })
      .then((res) => {
        logger.log("AuthCallback: Session verified, user:", res.data.user);
        if (setUser) setUser(res.data.user);
        // Also store token in context if backend returns it (for Bearer fallback)
        if (res.data.token) setToken(res.data.token);
      })
      .catch((err) => {
        logger.log(
          "AuthCallback: Session verification failed, redirecting to login",
          err,
        );
        navigate("/login");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setToken, setUser, navigate]);

  // Second effect to handle navigation after token verification
  useEffect(() => {
    // Only proceed if we have a token and we're not loading
    if (!token || loading) {
      logger.log("AuthCallback: Still loading or no token, waiting...");
      return;
    }

    logger.log("AuthCallback: Token verified, user data:", user);

    const isNewUser = params.get("new_user") === "true";

    if (isNewUser) {
      logger.log(
        "AuthCallback: New user detected, checking for saved access...",
      );
      const savedAccess = sessionStorage.getItem("moduleAccess");
      logger.log(
        "AuthCallback: Saved access from sessionStorage:",
        savedAccess,
      );

      if (savedAccess) {
        const accessPrefs = JSON.parse(savedAccess);
        updateUserAccess(token, accessPrefs);
        return; // Exit early, updateUserAccess will handle navigation
      }
    }

    // For all other cases (new user without saved access, existing user)
    // Navigate to dashboard with role-based logic
    logger.log("AuthCallback: Navigating with role-based logic");

    // UPDATED: Role-based redirect for all navigation cases
    const decoded = token ? jwtDecode(token) : null;
    const isTeacher = decoded && decoded.user_type === "teacher";

    logger.log(
      "AuthCallback: User role detected:",
      isTeacher ? "teacher" : "student",
    );
    logger.log(
      "AuthCallback: Redirecting to:",
      isTeacher ? "/teacher/dashboard" : "/dashboard",
    );

    navigate(isTeacher ? "/teacher/dashboard" : "/dashboard");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, loading, user, navigate, updateUserAccess]);

  return (
    <div>
      <p>Completing login...</p>
      <p>Please wait while we set up your account...</p>
    </div>
  );
}

export default AuthCallback;
